import { Box } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../../../components/common/BriaButton/BriaButton.tsx";
import BriaInput from "../../../../../../components/common/BriaInput/BriaInput.tsx";
import BriaSlider from "../../../../../../components/common/BriaSlider/BriaSlider.tsx";
import { ImageEditingFeatureDetector } from "../../../../../../constants/ImageEditingFeatures.ts";
import LinkConstants from "../../../../../../constants/LinkConstants.ts";
import { POSTHOG_EVENTS } from "../../../../../../constants/PostHogEvents";
import { IMAGE_EDITING_FEATURES } from "../../../../../../constants/routesMapper.ts";
import { useLogEvents } from "../../../../../../hooks/useLogEvents.ts";
import usePostHog from "../../../../../../hooks/usePostHog.tsx";
import { useAppStore } from "../../../../../../hooks/useStores.tsx";
import InputLayout from "../../../../../../layout/InputLayout/InputLayout.tsx";
import { ImageEditingConfigComponentProps, ImageEditingViewMode } from "../../../../../../models/image-to-image.ts";
import NewTgDropdown from "../../../../../NewTG/Models/Dropdown/NewTgDropdown.tsx";
import Config from "../../Config/Config.tsx";
import styles from "./EraseObject.module.scss";
import { SegmentationMaskTypes } from "./eraseObjectConstants.ts";
import { ObjectManipulationType } from "./eraseObjectTypes.ts";

const EraseObject = observer(({ onBackButtonClick }: ImageEditingConfigComponentProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.imageToImage.config.features.eraseObject" });
	const { imageToImageStore, playgroundStore } = useAppStore();
	const { editingCanvasStore } = imageToImageStore;
	const { trackEvent } = usePostHog();
	const { logEraseObjectEvent, logGenerativeFillEvent } = useLogEvents();
	const [isSingleMode] = useState<boolean>(playgroundStore.imageEditingViewMode === ImageEditingViewMode.SINGLE);
	const isGenerateFillDisabled =
		editingCanvasStore.isErasingImages ||
		!editingCanvasStore.isMaskApplied ||
		!editingCanvasStore.objectManipulationConfig?.prompt;

	useEffect(() => {
		if (!isSingleMode) {
			playgroundStore.togglePlaygroundViewMode();
		}
		playgroundStore.togglePlaygroundBrushMode(true);
		editingCanvasStore.toggleEraseConfigs();

		return () => {
			playgroundStore.togglePlaygroundBrushMode(false);
			editingCanvasStore.toggleEraseConfigs();
			editingCanvasStore.toggleIsMaskApplied(false);
			handleEraseConfigChange("erase", false);
			handleEraseConfigChange("gen_fill", false);
			handleEraseConfigChange("mask_type", SegmentationMaskTypes.MANUAL);
		};
	}, []);

	useEffect(() => {
		if (!(playgroundStore.imageEditingViewMode === ImageEditingViewMode.SINGLE)) {
			onBackButtonClick?.();
		}
	}, [playgroundStore.imageEditingViewMode]);

	const handleEraseClick = async () => {
		const selectedImage =
			playgroundStore.singleSelectedImage || playgroundStore.getAvailableImages().find((image) => image.selected);
		if (selectedImage) {
			const startTime = Date.now();
			const featureNames = ImageEditingFeatureDetector.getFeatureDisplayNames();
			const eraseFeature = featureNames[IMAGE_EDITING_FEATURES.ERASE_OBJECT];

			// Common event properties for this action
			const commonEventProps = {
				feature: eraseFeature,
				mode: ImageEditingViewMode.SINGLE,
			};

			// Track erase object started
			trackEvent(POSTHOG_EVENTS.IMAGE_EDITING_STARTED, commonEventProps);

			try {
				handleEraseConfigChange("erase", true);
				handleEraseConfigChange("gen_fill", false);
				playgroundStore.togglePlaygroundBrushMode(false);
				await editingCanvasStore.eraseObjectV2(selectedImage);

				handleEraseConfigChange("mask_type", SegmentationMaskTypes.MANUAL);

				// Track successful erase
				const processingTime = Date.now() - startTime;
				trackEvent(POSTHOG_EVENTS.IMAGE_EDITING_COMPLETED, {
					...commonEventProps,
					processing_time: processingTime,
					send_instantly: true,
				});

				logEraseObjectEvent();
			} catch (e) {
				// Track failed erase
				trackEvent(POSTHOG_EVENTS.IMAGE_EDITING_FAILED, {
					...commonEventProps,
					error: e instanceof Error ? e.message : String(e),
					send_instantly: true,
				});

				throw new Error(`Error erasing object: ${e}`);
			} finally {
				editingCanvasStore.isEraseConfigsEnabled && playgroundStore.togglePlaygroundBrushMode(true);
			}
		}
	};

	const handleEraseConfigChange = <K extends keyof ObjectManipulationType>(
		field: K,
		value: ObjectManipulationType[K],
	) => {
		editingCanvasStore.objectManipulationConfig = {
			...editingCanvasStore.objectManipulationConfig,
			[field]: value,
		};
	};

	const handleEraseWithReplaceByTextClick = async () => {
		const selectedImage = playgroundStore.singleSelectedImage || playgroundStore.getAvailableImages()[0];
		if (selectedImage) {
			const startTime = Date.now();
			const featureNames = ImageEditingFeatureDetector.getFeatureDisplayNames();
			const genFillFeature = featureNames[IMAGE_EDITING_FEATURES.GENERATIVE_FILL];

			// Common event properties for this action
			const commonEventProps = {
				feature: genFillFeature,
				mode: ImageEditingViewMode.SINGLE,
			};

			// Track generative fill started
			trackEvent(POSTHOG_EVENTS.IMAGE_EDITING_STARTED, commonEventProps);

			try {
				playgroundStore.togglePlaygroundBrushMode(false);
				handleEraseConfigChange("erase", false);
				handleEraseConfigChange("gen_fill", true);
				await editingCanvasStore.callGenFillApiV2(selectedImage, imageToImageStore.config);
				handleEraseConfigChange("mask_type", SegmentationMaskTypes.MANUAL);

				// Track successful generative fill
				const processingTime = Date.now() - startTime;
				trackEvent(POSTHOG_EVENTS.IMAGE_EDITING_COMPLETED, {
					...commonEventProps,
					processing_time: processingTime,
					send_instantly: true,
				});

				logGenerativeFillEvent();
			} catch (e) {
				// Track failed generative fill
				trackEvent(POSTHOG_EVENTS.IMAGE_EDITING_FAILED, {
					...commonEventProps,
					error: e instanceof Error ? e.message : String(e),
					send_instantly: true,
				});

				throw new Error(`Error erasing object: ${e}`);
			} finally {
				editingCanvasStore.isEraseConfigsEnabled && playgroundStore.togglePlaygroundBrushMode(true);
			}
		}
	};

	return (
		<Box className={styles.eraseObjectConfigContainer}>
			<Config
				title={t("removeObject")}
				titleOnly
				isActive
				className={styles.removeObjectConfig}
				apiDocLink={LinkConstants.BRIA_API_REMOVE_OBJECT}
			>
				<BriaButton
					buttonType="primaryMedium"
					disabled={editingCanvasStore.isErasingImages || !editingCanvasStore.isMaskApplied}
					onClick={handleEraseClick}
					size="small"
					className={styles.eraseObjectBtn}
				>
					{t("remove")}
				</BriaButton>
			</Config>
			<Config
				title={t("replaceObject")}
				titleOnly
				isActive
				className={styles.replaceObjectConfig}
				tooltip={t("replaceObjectTooltip")}
				apiDocLink={LinkConstants.BRIA_API_GEN_FILL}
			>
				<BriaInput
					value={editingCanvasStore.objectManipulationConfig?.prompt}
					placeholder={t("replaceObjectPlaceholder")}
					onChange={(e) => {
						const target = e.target as HTMLInputElement;
						handleEraseConfigChange("prompt", target.value.replace(/\n/g, ""));
					}}
					multiline
					minRows={2}
					{...(!isGenerateFillDisabled && { onEnterPress: handleEraseWithReplaceByTextClick })}
				/>
				<InputLayout
					label={t("tailoredModel.label")}
					info={t("tailoredModel.info")}
					actionButton={
						<BriaButton
							buttonType="textSmall"
							className={styles.removeButton}
							onClick={() => {
								handleEraseConfigChange("tailored_model_id", undefined);
								handleEraseConfigChange("tailored_model_version", undefined);
							}}
							disabled={!editingCanvasStore.objectManipulationConfig?.tailored_model_id}
						>
							{t("tailoredModel.remove")}
						</BriaButton>
					}
				>
					<NewTgDropdown
						value={editingCanvasStore.objectManipulationConfig?.tailored_model_id}
						onChange={(_e, item) => {
							handleEraseConfigChange("tailored_model_id", item.value);
							handleEraseConfigChange("tailored_model_version", item.extraData?.training_version);
						}}
						alwaysRefresh
						placeholder={t("tailoredModel.placeholder")}
					/>
				</InputLayout>
				<InputLayout label={t("numOfResults")}>
					<BriaSlider
						value={editingCanvasStore.objectManipulationConfig?.num_results}
						className={styles.numberSlider}
						onChange={(_e, value) => {
							handleEraseConfigChange("num_results", value as number);
						}}
						min={1}
						step={1}
						max={4}
						marks={Array.from({ length: 4 }, (_, index) => ({
							value: index + 1,
							label: `${index + 1}`,
						}))}
					/>
				</InputLayout>
				<Box className={styles.eraseObjectGenerate}>
					<BriaButton
						buttonType="primaryMedium"
						disabled={isGenerateFillDisabled}
						onClick={handleEraseWithReplaceByTextClick}
						size="small"
						className={styles.eraseObjectBtn}
					>
						{t("generate")}
					</BriaButton>
				</Box>
			</Config>
		</Box>
	);
});

export default EraseObject;
